// File: frontend/src/components/RelatedReports.tsx
// "Related Reports" panel for the report detail page.
// Ranks other reports by shared canonical entities from the Knowledge Graph
// (IDF-weighted server-side, so commodity tools don't dominate).
import React from 'react';
import useSWR from 'swr';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Stack,
    Skeleton,
    alpha,
} from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useApi } from '../hooks/useApi';
import { cyber } from '../theme';

interface RelatedSharedEntity {
    id?: string | null;
    name: string;
    type: string;
}

interface RelatedReportItem {
    report_id: string;
    title: string | null;
    date: string | null;
    source: string | null;
    shared_count: number;
    score: number;
    shared_entities: RelatedSharedEntity[];
}

interface KGRelatedReportsResponse {
    report_id: string;
    total: number;
    related: RelatedReportItem[];
}

const TYPE_COLORS: { [key: string]: string } = {
    ThreatActor: '#ff5c5c',
    Malware: '#ff9f43',
    Tool: '#54a0ff',
    AttackPattern: '#a55eea',
    Indicator: '#10ac84',
    Vulnerability: '#f368e0',
};

const RelatedReports: React.FC<{ reportId: string }> = ({ reportId }) => {
    const { authenticatedFetcher } = useApi();
    const { data, error, isLoading } = useSWR<KGRelatedReportsResponse>(
        reportId ? `/api/kg/related-reports/${reportId}?limit=5` : null,
        authenticatedFetcher,
        { revalidateOnFocus: false, shouldRetryOnError: false }
    );

    // The panel is an enrichment: stay silent if the KG is unavailable
    if (error || (data && data.related.length === 0)) return null;

    return (
        <Paper sx={{ mt: 3, p: { xs: 2, md: 3 }, border: `1px solid ${alpha(cyber.primary, 0.2)}`, borderRadius: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                <HubIcon sx={{ color: cyber.primaryLight, fontSize: 26 }} />
                <Typography variant="h6" sx={{ color: cyber.primaryLight, fontWeight: 700 }}>
                    Related Reports
                </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Reports connected to this one through shared entities in the Knowledge Graph — rare
                shared entities (specific IOCs, malware) weigh more than common tooling.
            </Typography>

            {isLoading && (
                <Stack spacing={1}>
                    <Skeleton variant="rounded" height={64} />
                    <Skeleton variant="rounded" height={64} />
                </Stack>
            )}

            <Stack spacing={1}>
                {data?.related.map((item) => (
                    <Paper
                        key={item.report_id}
                        component={RouterLink}
                        to={`/report/${item.report_id}`}
                        sx={{
                            p: 1.5,
                            display: 'block',
                            textDecoration: 'none',
                            bgcolor: alpha(cyber.primary, 0.04),
                            border: `1px solid ${alpha(cyber.primary, 0.12)}`,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                bgcolor: alpha(cyber.primary, 0.09),
                                transform: 'translateX(4px)',
                            },
                        }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight="bold" color="text.primary" noWrap>
                                    {item.title || item.report_id}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 0.5 }} flexWrap="wrap" useFlexGap>
                                    {item.source && (
                                        <Typography variant="caption" sx={{ color: cyber.accent.cyan, fontFamily: cyber.fontMono }}>
                                            {item.source}
                                        </Typography>
                                    )}
                                    {item.date && (
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(item.date).toLocaleDateString()}
                                        </Typography>
                                    )}
                                    <Chip
                                        label={`${item.shared_count} shared entities`}
                                        size="small"
                                        sx={{ height: 18, fontSize: '0.65rem', color: cyber.accent.cyan, bgcolor: alpha(cyber.accent.cyan, 0.1) }}
                                    />
                                </Stack>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {item.shared_entities.slice(0, 6).map((e, i) => {
                                        const color = TYPE_COLORS[e.type] || '#9fa4c0';
                                        return (
                                            <Chip
                                                key={i}
                                                label={e.name.length > 32 ? `${e.name.slice(0, 32)}…` : e.name}
                                                size="small"
                                                // Chips link to the entity profile; stop propagation so
                                                // the surrounding report card link doesn't also fire.
                                                {...(e.id ? {
                                                    component: RouterLink,
                                                    to: `/entity/${e.id}`,
                                                    clickable: true,
                                                    onClick: (ev: React.MouseEvent) => ev.stopPropagation(),
                                                } : {})}
                                                sx={{
                                                    height: 18,
                                                    fontSize: '0.65rem',
                                                    color,
                                                    bgcolor: alpha(color, 0.12),
                                                    border: `1px solid ${alpha(color, 0.3)}`,
                                                    ...(e.id ? { '&:hover': { bgcolor: alpha(color, 0.25) } } : {}),
                                                }}
                                            />
                                        );
                                    })}
                                </Stack>
                            </Box>
                            <ArrowForwardIcon sx={{ color: alpha(cyber.primaryLight, 0.6), flexShrink: 0 }} />
                        </Stack>
                    </Paper>
                ))}
            </Stack>
        </Paper>
    );
};

export default RelatedReports;
